import React from "react";
import { useNavigate } from "react-router-dom";
import { usePlayer } from "../context/PlayerContext";
import { Avatar } from "./Avatar";
import { mediaUrl, api, getToken, API } from "../lib/api";
import { Play, Pause, Heart, Flame, X, Download, MessageCircle, Music } from "lucide-react";
import { useSiteImages } from "../context/SiteImagesContext";
import { toast } from "sonner";

export function ProposalCard({ item, onReact, onDownloaded, showReactions = true, searchId }) {
  const nav = useNavigate();
  const { track, playing, play } = usePlayer();
  const { img } = useSiteImages();
  const prod = item.production;
  const bm = item.beatmaker;
  const subId = item.submission_id || item.id;
  const isCurrent = track && track.id === subId;
  const cover = mediaUrl(prod, "image") || img("default_cover");

  const doPlay = () => play({ id: subId, title: prod.title, subtitle: `@${bm?.pseudo}`, production: prod, cover });

  const download = () => {
    const url = `${API}/submissions/${subId}/download?auth=${getToken()}`;
    const a = document.createElement("a");
    a.href = url; a.download = `${prod.title}.${prod.ext || "mp3"}`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Téléchargement lancé");
    onDownloaded && onDownloaded(subId);
  };

  const react = async (type) => {
    try {
      await api.post("/reactions", { submission_id: subId, type });
      onReact && onReact(subId, type);
    } catch { toast.error("Erreur"); }
  };

  const contact = async () => {
    try {
      const { data } = await api.post("/conversations", { other_user_id: bm.id, search_id: searchId });
      nav(`/app/messages/${data.id}`);
    } catch { toast.error("Impossible d'ouvrir la discussion"); }
  };

  const rbtn = (type, Icon, color) => (
    <button onClick={() => react(type)} data-testid={`reaction-${type}-button`}
      className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all ${
        item.reaction === type ? `${color} border-transparent text-white scale-105` : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
      <Icon size={17} fill={item.reaction === type ? "white" : "none"} />
    </button>
  );

  return (
    <div className="bg-[#0B0716] border border-zinc-800/80 rounded-2xl p-4 animate-fade-up neon-card" data-testid={`proposal-${subId}`}>
      <div className="flex gap-4">
        <button onClick={doPlay} data-testid={`play-${subId}`}
          className="relative w-16 h-16 rounded-xl overflow-hidden shrink-0 group">
          {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> :
            <div className="w-full h-full bg-gradient-to-br from-violet-700 to-purple-900 flex items-center justify-center"><Music size={22} /></div>}
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            {isCurrent && playing ? <Pause size={22} className="text-white" fill="white" /> : <Play size={22} className="text-white ml-0.5" fill="white" />}
          </div>
          {isCurrent && playing && (
            <div className="absolute bottom-1 right-1 bar-eq text-violet-300"><span></span><span></span><span></span><span></span></div>
          )}
        </button>
        <div className="min-w-0 flex-1">
          <h4 className="font-display font-semibold text-base truncate">{prod.title}</h4>
          <button onClick={() => bm?.id && nav(`/app/beatmaker/${bm.id}`)} className="flex items-center gap-1.5 mt-1 text-zinc-400 hover:text-violet-300 transition-colors">
            <Avatar user={bm} size={18} /> <span className="text-xs">@{bm?.pseudo}</span>
          </button>
          <div className="flex gap-3 mt-1.5 text-[11px] font-mono text-zinc-500">
            {prod.style && <span>{prod.style}</span>}
            {prod.bpm && <span>{prod.bpm} BPM</span>}
            {prod.key && <span>{prod.key}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-zinc-800">
        {showReactions && (
          <div className="flex gap-2">
            {rbtn("love", Heart, "bg-red-500")}
            {rbtn("fire", Flame, "bg-orange-500")}
            {rbtn("reject", X, "bg-zinc-600")}
          </div>
        )}
        <div className="flex gap-2 ml-auto">
          <button onClick={download} data-testid={`download-${subId}`}
            className="w-10 h-10 rounded-full border border-zinc-700 flex items-center justify-center text-zinc-300 hover:border-violet-500 hover:text-violet-300 transition-all">
            <Download size={17} />
          </button>
          <button onClick={contact} data-testid={`contact-${subId}`}
            className="flex items-center gap-1.5 px-4 h-10 rounded-full bg-violet-600/20 border border-violet-600/40 text-violet-300 text-sm font-medium hover:bg-violet-600/30 transition-all">
            <MessageCircle size={15} /> Discuter
          </button>
        </div>
      </div>
    </div>
  );
}
